import { Head, router } from "@inertiajs/react";
import { AlertDialog, Box, Button, Callout, Flex, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";

import { AppLayout } from "@/components/app-layout";
import type { AuthUser } from "@/lib/types";

type TimeTravelShowProps = {
  auth: { user: AuthUser | null };
  flash?: { notice?: string; alert?: string };
  offsetWeeks: number;
  currentWeekStartOn: string;
  todayIso8601: string;
  realTodayIso8601: string;
  realCurrentWeekStartOn: string;
  sandboxResetEnabled: boolean;
};

export default function TimeTravelShow({
  auth,
  flash,
  offsetWeeks,
  currentWeekStartOn,
  todayIso8601,
  realTodayIso8601,
  realCurrentWeekStartOn,
  sandboxResetEnabled,
}: TimeTravelShowProps) {
  const [sandboxConfirmOpen, setSandboxConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  function postAction(path: string, label: string) {
    setBusy(label);
    router.post(path, {}, { onFinish: () => setBusy(null) });
  }

  return (
    <AppLayout user={auth.user} activeModule="weekly_planner" onModuleChange={() => {}} pageTitle="Time travel">
      <Head title="Time travel" />
      <Box p="4" className="mx-auto max-w-3xl space-y-4">
        <Heading size="6">Time travel (dev/staging)</Heading>
        {flash?.notice ? (
          <Callout.Root color="green">
            <Callout.Text>{flash.notice}</Callout.Text>
          </Callout.Root>
        ) : null}
        {flash?.alert ? (
          <Callout.Root color="red">
            <Callout.Text>{flash.alert}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Callout.Root>
          <Callout.Text>
            Move the app clock forward one week at a time for everyone on this environment (stored in Redis).
            Test week rollover, Harvest hour burn-down, and Copy Previous Week with non-admin users. You cannot
            travel backward. Use Reset sandbox when finished — that is the only way to restore real time and a clean database.
          </Callout.Text>
        </Callout.Root>

        <Box className="space-y-2 rounded-md border p-4">
          <Text weight="bold">App clock</Text>
          <Text size="2" color="gray">
            Offset: {offsetWeeks} week{offsetWeeks === 1 ? "" : "s"}
          </Text>
          <Text size="2">
            Effective today: {todayIso8601} (week of {currentWeekStartOn})
          </Text>
          <Text size="2" color="gray">
            Real today: {realTodayIso8601} (week of {realCurrentWeekStartOn})
          </Text>
        </Box>

        <Flex gap="2" wrap="wrap">
          <Button disabled={busy !== null} onClick={() => postAction("/admin/time_travel/advance", "advance")}>
            {busy === "advance" ? "Advancing…" : "Advance app clock +1 week"}
          </Button>
          {sandboxResetEnabled ? (
            <Button color="red" variant="soft" disabled={busy !== null} onClick={() => setSandboxConfirmOpen(true)}>
              Reset sandbox to baseline…
            </Button>
          ) : (
            <Text size="2" color="gray">
              Sandbox reset disabled (SANDBOX_RESET_ENABLED=false).
            </Text>
          )}
        </Flex>

        <Text size="2" color="gray">
          Reset sandbox truncates the database, re-seeds, clears the shared clock offset and mock scenarios, and
          restores MockServer baseline — same outcome as <code>docker compose down -v</code> on dev or{" "}
          <code>db:truncate_all db:seed</code> on staging.
        </Text>
      </Box>

      <AlertDialog.Root open={sandboxConfirmOpen} onOpenChange={setSandboxConfirmOpen}>
        <AlertDialog.Content maxWidth="520px">
          <AlertDialog.Title>Reset sandbox to baseline?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Deletes all projects, planner data, and non-seed users; clears Redis mock scenarios; reapplies
            MockServer initializer; resets app clock. Affects all users on this host. You will sign in again.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                color="red"
                onClick={() => {
                  setSandboxConfirmOpen(false);
                  postAction("/admin/time_travel/reset_sandbox", "reset_sandbox");
                }}
              >
                Reset sandbox
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </AppLayout>
  );
}
